<!--
  SelectDropdown.vue — select personalizado (clic para abrir, ~5 renglones
  visibles con scroll) cuyo panel se teletransporta a <body> con position:
  fixed, calculado desde el trigger. A diferencia del patrón
  "custom-select-wrapper" usado en otras pantallas (position: absolute
  dentro del propio contenedor), este NO se recorta cuando vive dentro de
  un modal angosto o con scroll propio (ej. una tabla de renglones
  dinámicos) — útil para formularios con muchos selects apilados en poco
  espacio vertical.
-->
<template>
  <div class="sd-wrapper" ref="wrapperRef">
    <div class="custom-select-input" :class="{ 'sd-dense': dense }" @click="toggle">
      <template v-if="selected">
        <slot name="selected" :option="selected">
          <span>{{ optionLabel(selected) }}</span>
        </slot>
      </template>
      <span v-else class="text-muted">{{ placeholder }}</span>
    </div>
    <i
      class="bi custom-select-arrow"
      :class="[abierto ? 'bi-chevron-up' : 'bi-chevron-down', { 'sd-dense-arrow': dense }]"
      @mousedown.prevent="toggle"
    ></i>

    <Teleport to="body">
      <div
        v-if="abierto"
        ref="panelRef"
        class="custom-dropdown sd-panel"
        :class="{ 'sd-panel--dense': dense }"
        :style="panelStyle"
      >
        <div
          v-for="opt in options"
          :key="optionValue(opt)"
          class="custom-dropdown-item"
          :class="{ selected: optionValue(opt) === modelValue }"
          @mousedown.prevent="elegir(opt)"
        >
          <slot name="option" :option="opt">{{ optionLabel(opt) }}</slot>
        </div>
        <div v-if="!options.length" class="custom-dropdown-item text-muted">Sin resultados</div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onBeforeUnmount } from "vue";

const props = defineProps({
  modelValue: { default: "" },
  options: { type: Array, default: () => [] },
  optionValue: { type: Function, required: true },
  optionLabel: { type: Function, required: true },
  placeholder: { type: String, default: "Selecciona..." },
  // Variante compacta para contextos angostos (ej. celdas de una tabla de
  // renglones dinámicos), independiente del tamaño normal usado en
  // formularios — así un ajuste en una pantalla no mueve el otro.
  dense: { type: Boolean, default: false },
});
const emit = defineEmits(["update:modelValue"]);

const abierto = ref(false);
const wrapperRef = ref(null);
const panelRef = ref(null);
const panelStyle = ref({});

const selected = computed(() =>
  props.options.find((o) => props.optionValue(o) === props.modelValue)
);

function posicionar() {
  if (!wrapperRef.value) return;
  const rect = wrapperRef.value.getBoundingClientRect();
  const alturaPanel = 185;
  const espacioAbajo = window.innerHeight - rect.bottom;
  const haciaArriba = espacioAbajo < alturaPanel + 12 && rect.top > alturaPanel;
  panelStyle.value = {
    position: "fixed",
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    minWidth: "220px",
    ...(haciaArriba
      ? { bottom: `${window.innerHeight - rect.top + 4}px` }
      : { top: `${rect.bottom + 4}px` }),
  };
}

function onOutside(e) {
  if (
    wrapperRef.value &&
    !wrapperRef.value.contains(e.target) &&
    panelRef.value &&
    !panelRef.value.contains(e.target)
  ) {
    cerrar();
  }
}
function onScroll(e) {
  // Ignora el scroll interno de la propia lista del panel.
  if (panelRef.value && e.target && panelRef.value.contains(e.target)) return;
  cerrar();
}

function abrir() {
  abierto.value = true;
  nextTick(() => {
    posicionar();
    window.addEventListener("mousedown", onOutside, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", posicionar);
  });
}
function cerrar() {
  if (!abierto.value) return;
  abierto.value = false;
  window.removeEventListener("mousedown", onOutside, true);
  window.removeEventListener("scroll", onScroll, true);
  window.removeEventListener("resize", posicionar);
}
function toggle() {
  if (abierto.value) cerrar();
  else abrir();
}
function elegir(opt) {
  emit("update:modelValue", props.optionValue(opt));
  cerrar();
}

onBeforeUnmount(cerrar);
</script>

<style scoped>
.sd-wrapper {
  position: relative;
}
.custom-select-input {
  border: 1px solid var(--d-green);
  border-radius: 8px;
  padding: 0 2.2rem 0 12px;
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  background: white;
  font-size: 0.95rem;
  overflow: hidden;
}
.custom-select-input span {
  overflow: hidden;
  text-overflow: ellipsis;
}
.custom-select-input.sd-dense {
  min-height: 34px;
  padding: 0 1.8rem 0 8px;
  font-size: 0.85rem;
}
.custom-select-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  font-size: 0.8rem;
  cursor: pointer;
}
.custom-select-arrow.sd-dense-arrow {
  right: 8px;
  font-size: 0.72rem;
}
</style>

<style>
/* El panel se teletransporta a <body>, fuera del scope del componente —
   por eso estas reglas (incluido .custom-dropdown-item, que también usa
   el panel) van sin scope. */
.sd-panel {
  z-index: 2000;
  max-height: 185px;
  overflow-y: auto;
  background: white;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(40, 54, 24, 0.15);
}
.sd-panel .custom-dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  gap: 8px;
  align-items: center;
  border-bottom: 1px solid rgba(96, 108, 56, 0.08);
  transition: background 0.12s;
  font-size: 0.85rem;
  color: var(--d-brown);
  white-space: nowrap;
  overflow: hidden;
}
.sd-panel .custom-dropdown-item > span {
  overflow: hidden;
  text-overflow: ellipsis;
}
.sd-panel .custom-dropdown-item:last-child {
  border-bottom: none;
}
.sd-panel .custom-dropdown-item:hover,
.sd-panel .custom-dropdown-item.selected {
  background: rgba(96, 108, 56, 0.12);
}
.sd-panel--dense .custom-dropdown-item {
  padding: 6px 10px;
  font-size: 0.8rem;
}
</style>
